import { useState } from 'react'
import { api } from '../../api/client'
import { useQuery } from '../../api/hooks'
import type { AppUserRow, RoleRow } from '../../api/types'
import { Empty, ErrorBanner, Field, Loading, Modal, Panel, Tag, formatDateTime } from '../../components/ui'

export function UsersPage() {
  const users = useQuery<AppUserRow[]>('/admin/users')
  const roles = useQuery<RoleRow[]>('/admin/roles')

  const [editing, setEditing] = useState<AppUserRow | null>(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<unknown>()

  async function act(path: string, body?: unknown) {
    setError(undefined)
    try {
      await api(path, { method: 'POST', body })
      users.reload()
    } catch (caught) {
      setError(caught)
    }
  }

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="spacer" />
        <button className="primary" onClick={() => setCreating(true)}>
          + New user
        </button>
      </div>

      <ErrorBanner error={error ?? users.error} onDismiss={() => setError(undefined)} />
      {users.loading && <Loading />}

      {users.data && (
        <Panel flush>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th style={{ width: 220 }}>Roles</th>
                <th style={{ width: 160 }}>Last signed in</th>
                <th style={{ width: 150 }}>Status</th>
                <th style={{ width: 210 }} />
              </tr>
            </thead>
            <tbody>
              {users.data.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <Empty>No users.</Empty>
                  </td>
                </tr>
              )}

              {users.data.map((user) => (
                <tr key={user.id} style={{ opacity: user.isActive ? 1 : 0.5 }}>
                  <td className="strong">{user.fullNameEn}</td>
                  <td className="soft">{user.email}</td>
                  <td>
                    {user.roles.map((role) => (
                      <Tag key={role} kind="info">
                        {role}
                      </Tag>
                    ))}
                  </td>
                  <td className="soft">{formatDateTime(user.lastLoginAt) || 'never'}</td>
                  <td>
                    {!user.isActive && <Tag kind="danger">inactive</Tag>}{' '}
                    {user.isLockedOut && <Tag kind="danger">locked out</Tag>}{' '}
                    {user.mustChangePassword && <Tag kind="warn">must change password</Tag>}
                  </td>
                  <td>
                    <button className="subtle small" onClick={() => setEditing(user)}>
                      Edit
                    </button>
                    <button
                      className="subtle small"
                      onClick={() => {
                        const password = window.prompt(`New password for ${user.email}:`)
                        if (password) {
                          void act(`/admin/users/${user.id}/reset-password`, { newPassword: password })
                        }
                      }}
                    >
                      Reset password
                    </button>
                    {user.isLockedOut && (
                      <button className="subtle small" onClick={() => void act(`/admin/users/${user.id}/unlock`)}>
                        Unlock
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {(creating || editing) && roles.data && (
        <UserForm
          user={editing}
          roles={roles.data}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSaved={() => {
            setCreating(false)
            setEditing(null)
            users.reload()
          }}
        />
      )}
    </>
  )
}

function UserForm({
  user,
  roles,
  onClose,
  onSaved,
}: {
  user: AppUserRow | null
  roles: RoleRow[]
  onClose: () => void
  onSaved: () => void
}) {
  const [email, setEmail] = useState(user?.email ?? '')
  const [password, setPassword] = useState('')
  const [fullNameEn, setFullNameEn] = useState(user?.fullNameEn ?? '')
  const [fullNameAr, setFullNameAr] = useState(user?.fullNameAr ?? '')
  const [selected, setSelected] = useState<string[]>(user?.roles ?? [])
  const [isActive, setIsActive] = useState(user?.isActive ?? true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()

  function toggleRole(name: string) {
    setSelected((previous) =>
      previous.includes(name) ? previous.filter((r) => r !== name) : [...previous, name],
    )
  }

  async function save() {
    setBusy(true)
    setError(undefined)

    try {
      if (user) {
        await api(`/admin/users/${user.id}`, {
          method: 'PUT',
          body: {
            fullNameEn,
            fullNameAr: fullNameAr || null,
            roles: selected,
            isActive,
            partyId: null,
            preferredLanguage: user.preferredLanguage,
          },
        })
      } else {
        await api('/admin/users', {
          method: 'POST',
          body: {
            email,
            password,
            fullNameEn,
            fullNameAr: fullNameAr || null,
            roles: selected,
            partyId: null,
            preferredLanguage: 'en',
          },
        })
      }

      onSaved()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={user ? `Edit ${user.email}` : 'New user'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={busy || !fullNameEn} onClick={() => void save()}>
            {busy ? 'Saving…' : user ? 'Save' : 'Create'}
          </button>
        </>
      }
    >
      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />

      {!user && (
        <div className="banner info">
          The password you set here is a bootstrap credential — you will know it, so the user is
          forced to replace it at first sign-in.
        </div>
      )}

      <div className="grid cols-2">
        <Field label="Email">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!!user} />
        </Field>

        {!user && (
          <Field label="Initial password" hint="At least 10 characters, with a digit and a lower-case letter.">
            <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
        )}

        <Field label="Name (English)">
          <input value={fullNameEn} onChange={(e) => setFullNameEn(e.target.value)} />
        </Field>

        <Field label="Name (Arabic)">
          <input dir="rtl" value={fullNameAr} onChange={(e) => setFullNameAr(e.target.value)} />
        </Field>
      </div>

      <label>Roles</label>
      {roles.map((role) => (
        <div className="checklist-item" key={role.id}>
          <input
            type="checkbox"
            checked={selected.includes(role.name)}
            onChange={() => toggleRole(role.name)}
          />
          <div className="label">
            <strong>{role.name}</strong>
            <div className="muted" style={{ fontSize: 11 }}>
              {role.description} · {role.permissions.length} permissions
            </div>
          </div>
        </div>
      ))}

      {user && (
        <label style={{ textTransform: 'none', fontWeight: 400, fontSize: 13, marginTop: 12 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginRight: 6 }}
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          Account is active
        </label>
      )}
    </Modal>
  )
}
